import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { StockMovement } from '../../../models/StockMovement';
import { Item } from '../../../models/Item';
import { StockMovementsService, StockMovementPagedResult } from '../../../services/stock-movements.service';
import { ItemService } from '../../../services/item.service';
import {
  canLinkPendingItemToStockEntry,
  resolvePendingItemEntryIdForLink
} from './stock-movement-pending-item-link.helper';
import { PaginationOptions } from '../../../shared/interfaces/pagination.interface';
import { StockService } from '../../../services/stock.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { Stock } from '../../../models/Stock';
import { ErrorService } from '../../../shared/services/error.service';

@Component({
  selector: 'app-stock-movements',
  templateUrl: './stock-movements.component.html',
  styleUrls: ['./stock-movements.component.css']
})
export class StockMovementsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isServiceOrderRequestMode = false;
  /** 'entry' = fluxo de entrada (lista entry-receipt); 'all' = GET /stock-movements (incl. saídas) */
  movementsListMode: 'entry' | 'all' | 'requests' = 'entry';

  // ===== DADOS =====
  stockMovements: StockMovement[] = [];
  filteredData: StockMovement[] = [];
  stocks: Stock[] = [];
  isLoading = false;
  error: string | null = null;

  // ===== PAGINAÇÃO =====
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // ===== FILTROS =====
  filters = {
    movementType: '',
    status: '',
    stockId: '',
    startDate: '',
    endDate: ''
  };

  /** Modal: vincular item de estoque a entrada pendente (pedido / solicitação). */
  showLinkItemModal = false;
  linkItemModalPendingEntryId: number | null = null;
  linkItemModalContext = '';
  linkItemSelectedId: number | null = null;
  linkItemSearchTerm = '';
  linkItemSearchResults: Item[] = [];
  linkItemSearchLoading = false;
  linkItemSubmitting = false;

  movementTypes = [
    { value: 'Entry', label: 'Entrada' },
    { value: 'Exit', label: 'Saída' },
    { value: 'Transfer', label: 'Transferência' },
    { value: 'Adjustment', label: 'Ajuste' }
  ];
  /** Filtro de status na lista de entradas (recebimento) */
  private readonly statusesEntrada: string[] = [
    'AwaitingReceipt', 'RecParcialPend', 'ReceiptCompleted', 'Received', 'Executed', 'Cancelled'
  ];
  /** Lista geral: inclui aprovado / pendente (ex.: saídas) */
  private readonly statusesTodas: string[] = [
    ...this.statusesEntrada,
    'Approved', 'Pending', 'Rejected', 'InProgress'
  ];
  statuses: string[] = this.statusesEntrada;

  // ===== CONFIGURAÇÃO DA TABELA =====
  tableTitle = 'Movimentações de Estoque';
  showSearch = true;
  searchPlaceholder = 'Buscar por item, código, responsável...';
  clickable = true;

  columns = [
    { titulo: 'ID', label: 'id' },
    { titulo: 'Item', label: 'itemName' },
    { titulo: 'Código', label: 'itemCode' },
    { titulo: 'Tipo', label: 'movementTypeLabel' },
    { titulo: 'Qtd. mov.', label: 'quantityLabel' },
    { titulo: 'Qtd. prevista', label: 'initialExpectedLabel' },
    { titulo: 'Recebido', label: 'receivedEntryLabel' },
    { titulo: 'Pendente', label: 'pendingLabel' },
    { titulo: 'Data', label: 'movementDateLabel' },
    { titulo: 'Status', label: 'statusLabel' },
    { titulo: 'Responsável', label: 'staffName' }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Confirmar Recebimento',
      icon: 'fas fa-circle-check',
      action: 'confirmReceipt',
      class: 'btn-success'
    }
  ];

  constructor(
    private stockMovementsService: StockMovementsService,
    private stockService: StockService,
    private itemService: ItemService,
    private errorService: ErrorService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.loadStocks();
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.applyListModeFromRouteData(data);
      this.configureByMode();
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadStocks(): void {
    this.stockService.getAll({ page: 1, pageSize: 1000 }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        this.stocks = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar estoques:', error);
      }
    });
  }

  private applyListModeFromRouteData(data: { listMode?: string }): void {
    const fromData = (data?.listMode as 'requests' | 'all' | 'entry') || undefined;
    const path = this.route.snapshot.routeConfig?.path || '';
    let mode: 'requests' | 'all' | 'entry' = 'entry';
    if (fromData === 'requests' || fromData === 'all' || fromData === 'entry') {
      mode = fromData;
    } else if (path === 'stock/requests') {
      mode = 'requests';
    } else if (path === 'stock/movements/todas' || path.endsWith('/movements/todas')) {
      mode = 'all';
    } else {
      mode = 'entry';
    }
    this.isServiceOrderRequestMode = mode === 'requests';
    this.movementsListMode = this.isServiceOrderRequestMode ? 'requests' : mode;
    this.filters = {
      movementType: this.movementsListMode === 'entry' ? 'Entry' : '',
      status: '',
      stockId: '',
      startDate: '',
      endDate: ''
    };
  }

  loadData(): void {
    this.isLoading = true;
    this.error = null;
    const options: PaginationOptions = { page: this.currentPage, pageSize: this.pageSize };

    const request$ = this.isServiceOrderRequestMode
      ? this.stockMovementsService.getServiceOrderStockRequests(options)
      : this.movementsListMode === 'all'
        ? this.stockMovementsService.getAllStockMovementsPage(options)
        : this.stockMovementsService.getAll(options);

    request$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: StockMovementPagedResult) => {
        this.stockMovements = (result.items || []).map(item => {
          const wLabel = this.getWarehouseListLabel(item);
          const osLabel = item.serviceOrderNumber
            ? String(item.serviceOrderNumber)
            : (item.serviceOrderId != null && item.serviceOrderId > 0 ? `OS #${item.serviceOrderId}` : '—');
          const row: any = {
            ...item,
            itemName: item.itemName || (item.itemId != null && item.itemId > 0 ? `Item #${item.itemId}` : '—'),
            itemCode: item.itemCode && String(item.itemCode).trim() ? item.itemCode : '—',
            stockNameLabel: item.stockName && String(item.stockName).trim() ? String(item.stockName) : '—',
            movementTypeLabel: this.getMovementTypeLabel(item.movementType),
            statusLabel: this.getStatusLabel(item.status),
            quantityLabel: this.formatQtyNumber(item.quantity),
            initialExpectedLabel: this.formatQtyNumber(item.initialExpectedQuantity),
            pendingLabel: this.formatQtyNumber(item.pendingQuantity),
            receivedEntryLabel: this.formatQtyNumber(item.entryReceiptReceived),
            movementDateLabel: this.formatListDateTime(item.movementDate),
            warehouseListLabel: wLabel,
            serviceOrderListLabel: osLabel
          };
          if (!this.isServiceOrderRequestMode) {
            row._actions = [
              {
                label: 'Visualizar',
                icon: 'fas fa-eye',
                action: 'view',
                class: 'btn-view',
                visible: true
              },
              ...(this.canConfirmReceipt(item)
                ? [
                  {
                    label: 'Confirmar Recebimento',
                    icon: 'fas fa-circle-check',
                    action: 'confirmReceipt',
                    class: 'btn-success',
                    visible: true
                  }
                ]
                : []),
              ...(this.movementsListMode === 'entry' && canLinkPendingItemToStockEntry(item)
                ? [
                  {
                    label: 'Vincular item',
                    icon: 'fas fa-link',
                    action: 'linkPendingItem',
                    class: 'btn-outline-primary',
                    visible: true
                  }
                ]
                : [])
            ];
          }
          return row;
        }) as StockMovement[];
        this.totalItems = result.totalItems;
        this.totalPages = result.totalPages;
        this.hasNextPage = this.currentPage < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.currentPage = result.page;
        this.pageSize = result.pageSize;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar movimentações:', error);
        this.error = this.isServiceOrderRequestMode
          ? 'Erro ao carregar solicitações de estoque da OS. Tente novamente.'
          : 'Erro ao carregar movimentações. Tente novamente.';
        this.isLoading = false;
        this.stockMovements = [];
        this.filteredData = [];
      }
    });
  }

  applyFilters(): void {
    let filtered = [...this.stockMovements];

    if (this.filters.stockId) {
      const stockIdTerm = this.filters.stockId.toString().trim().toLowerCase();
      filtered = filtered.filter(item =>
        String(item.itemId || '').toLowerCase().includes(stockIdTerm) ||
        (item.itemCode || '').toLowerCase().includes(stockIdTerm) ||
        (item.itemName || '').toLowerCase().includes(stockIdTerm)
      );
    }

    if (this.filters.movementType) {
      filtered = filtered.filter(item => item.movementType === this.filters.movementType);
    }

    if (this.filters.status) {
      filtered = filtered.filter(item => item.status === this.filters.status);
    }

    if (this.filters.startDate) {
      const start = new Date(this.filters.startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(item => {
        const movementDate = new Date(item.movementDate);
        return !Number.isNaN(movementDate.getTime()) && movementDate >= start;
      });
    }

    if (this.filters.endDate) {
      const end = new Date(this.filters.endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => {
        const movementDate = new Date(item.movementDate);
        return !Number.isNaN(movementDate.getTime()) && movementDate <= end;
      });
    }

    this.filteredData = filtered;
  }

  clearFilters(): void {
    this.filters = {
      movementType: this.movementsListMode === 'entry' ? 'Entry' : '',
      status: '',
      stockId: '',
      startDate: '',
      endDate: ''
    };
    this.applyFilters();
  }

  onSearch(term: string): void {
    if (!term) {
      this.applyFilters();
      return;
    }
    term = term.toLowerCase();
    this.filteredData = this.stockMovements.filter(
      (item) =>
        (item.itemCode && String(item.itemCode).toLowerCase().includes(term)) ||
        (item.itemName && String(item.itemName).toLowerCase().includes(term)) ||
        (item.status && String(item.status).toLowerCase().includes(term)) ||
        (item.reason && String(item.reason).toLowerCase().includes(term)) ||
        (item.staffName && String(item.staffName).toLowerCase().includes(term)) ||
        (item.createdBy && String(item.createdBy).toLowerCase().includes(term)) ||
        (this.movementsListMode === 'all' && item.stockName && String(item.stockName).toLowerCase().includes(term)) ||
        (this.movementsListMode === 'all' && item.serviceOrderNumber && String(item.serviceOrderNumber).toLowerCase().includes(term)) ||
        (this.movementsListMode === 'all' && item.serviceOrderId != null && String(item.serviceOrderId).includes(term))
    );
  }

  private configureByMode(): void {
    if (this.isServiceOrderRequestMode) {
      this.tableTitle = 'Solicitações de Estoque (OS)';
      this.searchPlaceholder = 'Buscar por número da solicitação ou status...';
      this.statuses = ['Requested', 'Pending', 'PartiallyFulfilled', 'Fulfilled', 'Cancelled'];
      this.columns = [
        { titulo: 'ID', label: 'id' },
        { titulo: 'Solicitação', label: 'itemCode' },
        { titulo: 'OS', label: 'serviceOrderNumber' },
        { titulo: 'Prioridade', label: 'reason' },
        { titulo: 'Status', label: 'status' },
        { titulo: 'Solicitada Em', label: 'movementDate' }
      ];
      this.actions = [
        {
          label: 'Visualizar',
          icon: 'fas fa-eye',
          action: 'view',
          class: 'btn-view'
        },
        {
          label: 'Atender',
          icon: 'fas fa-circle-check',
          action: 'fulfill',
          class: 'btn-success'
        }
      ];
      return;
    }

    if (this.movementsListMode === 'all') {
      this.tableTitle = 'Todas as movimentações';
      this.searchPlaceholder = 'Buscar por peça, item, OS, responsável, observações...';
      this.statuses = [...this.statusesTodas];
      this.columns = [
        { titulo: 'ID', label: 'id' },
        { titulo: 'Peça (estoque)', label: 'stockNameLabel' },
        { titulo: 'Item', label: 'itemName' },
        { titulo: 'Cód. item', label: 'itemCode' },
        { titulo: 'Tipo', label: 'movementTypeLabel' },
        { titulo: 'Qtd. mov.', label: 'quantityLabel' },
        { titulo: 'Almoxarifado', label: 'warehouseListLabel' },
        { titulo: 'OS', label: 'serviceOrderListLabel' },
        { titulo: 'Data', label: 'movementDateLabel' },
        { titulo: 'Status', label: 'statusLabel' },
        { titulo: 'Responsável', label: 'staffName' }
      ];
      this.actions = [
        {
          label: 'Visualizar',
          icon: 'fas fa-eye',
          action: 'view',
          class: 'btn-view'
        }
      ];
      return;
    }

    this.tableTitle = 'Entrada/Saída de Produtos';
    this.searchPlaceholder = 'Buscar por item, código, responsável...';
    this.statuses = [...this.statusesEntrada];
    this.columns = [
      { titulo: 'ID', label: 'id' },
      { titulo: 'Item', label: 'itemName' },
      { titulo: 'Código', label: 'itemCode' },
      { titulo: 'Tipo', label: 'movementTypeLabel' },
      { titulo: 'Qtd. mov.', label: 'quantityLabel' },
      { titulo: 'Qtd. prevista', label: 'initialExpectedLabel' },
      { titulo: 'Recebido', label: 'receivedEntryLabel' },
      { titulo: 'Pendente', label: 'pendingLabel' },
      { titulo: 'Data', label: 'movementDateLabel' },
      { titulo: 'Status', label: 'statusLabel' },
      { titulo: 'Responsável', label: 'staffName' }
    ];
    this.actions = [
      {
        label: 'Visualizar',
        icon: 'fas fa-eye',
        action: 'view',
        class: 'btn-view'
      },
      {
        label: 'Confirmar Recebimento',
        icon: 'fas fa-circle-check',
        action: 'confirmReceipt',
        class: 'btn-success'
      }
    ];
  }

  private getWarehouseListLabel(item: StockMovement): string {
    const a = item.itemWarehouseName;
    if (a && String(a).trim()) {
      return String(a);
    }
    if (item.originWarehouseName && String(item.originWarehouseName).trim()) {
      return String(item.originWarehouseName);
    }
    if (item.destinationWarehouseName && String(item.destinationWarehouseName).trim()) {
      return String(item.destinationWarehouseName);
    }
    return '—';
  }

  getMovementTypeLabel(type: string): string {
    const types: { [key: string]: string } = {
      'Entry': 'Entrada',
      'Exit': 'Saída',
      'Transfer': 'Transferência',
      'Adjustment': 'Ajuste'
    };
    return types[type] || type;
  }

  /** Rótulos para o filtro de status (valores = chaves da API) */
  get statusFilterOptions(): { value: string; label: string }[] {
    return this.statuses.map((s) => ({ value: s, label: this.getStatusLabel(s) }));
  }

  private formatQtyNumber(value: number | undefined | null): string {
    if (value === undefined || value === null || (typeof value === 'number' && Number.isNaN(value))) {
      return '—';
    }
    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
  }

  private formatListDateTime(iso: string | undefined): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      AwaitingReceipt: 'Aguardando Recebimento',
      AguardandoRec: 'Aguardando Recebimento',
      RecParcialPend: 'Recebimento parcial pendente',
      ReceiptCompleted: 'Recebimento concluído',
      Received: 'Recebido',
      Executed: 'Executado',
      Cancelled: 'Cancelado',
      Pending: 'Pendente',
      Approved: 'Aprovado',
      Rejected: 'Rejeitado',
      InProgress: 'Em andamento',
      Solicitado: 'Solicitado',
      InTransit: 'Em trânsito',
      EmTransito: 'Em trânsito',
      AguardandoCad: 'Aguardando cadastro'
    };
    if (status && !statusMap[status]) {
      const k = (status || '').toLowerCase();
      if (k === 'receiptcompleted') {
        return statusMap['ReceiptCompleted'];
      }
      if (k === 'recparcialpend') {
        return statusMap['RecParcialPend'];
      }
      if (k === 'aguardandorec' || k === 'awaitingrec') {
        return statusMap['AwaitingReceipt'];
      }
      if (k === 'aguardandocad' || (k.includes('aguardando') && k.includes('cad'))) {
        return statusMap['AguardandoCad'];
      }
    }
    return statusMap[status] || status;
  }

  private canConfirmReceipt(movement: StockMovement): boolean {
    return this.stockMovementsService.isOpenForEntryReceipt(movement);
  }

  onActionClick(event: { action: string, item: any }): void {
    if (this.isServiceOrderRequestMode) {
      const requestId = Number(event.item?.id);
      if (!requestId) {
        return;
      }

      this.router.navigate(['/stock/requests', requestId]);
      return;
    }

    switch (event.action) {
      case 'view':
        if (!event.item?.id) {
          this.errorService.showErrorMessage('Não foi possível identificar a movimentação.');
          return;
        }
        this.router.navigate(['/stock/movements', event.item.id, 'view']);
        break;
      case 'confirmReceipt':
        if (!event.item?.id) {
          this.errorService.showErrorMessage('Não foi possível identificar a movimentação.');
          return;
        }
        this.stockMovementsService.getById(Number(event.item.id))
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (movementDetail) => {
              if (!this.canConfirmReceipt(movementDetail)) {
                this.errorService.showErrorMessage(
                  'Não é possível registrar o recebimento desta movimentação no momento.'
                );
                return;
              }
              this.router.navigate(['/stock/movements', movementDetail.id, 'confirm-receipt']);
            },
            error: () => {
              this.errorService.showErrorMessage('Não foi possível carregar os detalhes da movimentação.');
            }
          });
        break;
      case 'linkPendingItem':
        this.openLinkItemModal(event.item as StockMovement);
        break;
    }
  }

  createNewMovement(): void {
    this.router.navigate(['/stock/movements/new']);
  }

  createNewEntryMovement(): void {
    if (this.isServiceOrderRequestMode) {
      return;
    }

    this.router.navigate(['/stock/movements/new'], {
      queryParams: { tipo: 'entrada' }
    });
  }

  createNewExitMovement(): void {
    if (this.isServiceOrderRequestMode) {
      return;
    }

    this.router.navigate(['/stock/movements/new'], {
      queryParams: { tipo: 'saida', fluxo: 'consumo' }
    });
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.filteredData = [...this.filteredData].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadData();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadData();
  }

  retryLoad(): void {
    this.loadData();
  }

  // ----- Vincular entrada pendente → item de estoque (PUT .../link-item) -----

  openLinkItemModal(m: StockMovement): void {
    const pendingId = resolvePendingItemEntryIdForLink(m);
    if (pendingId == null) return;
    this.linkItemModalPendingEntryId = pendingId;
    this.linkItemModalContext = `${m.itemName || 'Item'} — mov. #${m.id}`;
    this.linkItemSelectedId = null;
    this.linkItemSearchTerm = '';
    this.linkItemSearchResults = [];
    this.showLinkItemModal = true;
  }

  closeLinkItemModal(): void {
    if (this.linkItemSubmitting) return;
    this.showLinkItemModal = false;
    this.linkItemModalPendingEntryId = null;
  }

  searchLinkItems(): void {
    const q = (this.linkItemSearchTerm || '').trim();
    if (q.length < 2) {
      this.errorService.showInfoMessage('Digite ao menos 2 caracteres para buscar o item.');
      return;
    }
    this.linkItemSearchLoading = true;
    this.itemService
      .getAllItemsWithNameFilter(q, { page: 1, pageSize: 20 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.linkItemSearchResults = res.items || [];
          this.linkItemSearchLoading = false;
        },
        error: () => {
          this.linkItemSearchResults = [];
          this.linkItemSearchLoading = false;
        }
      });
  }

  selectLinkSearchItem(it: Item): void {
    this.linkItemSelectedId = it.id;
    this.linkItemSearchTerm = `${it.code} — ${it.name}`;
  }

  clearLinkItemSelection(): void {
    this.linkItemSelectedId = null;
    this.linkItemSearchTerm = '';
    this.linkItemSearchResults = [];
  }

  submitLinkPendingItem(): void {
    const pid = this.linkItemModalPendingEntryId;
    const iid = this.linkItemSelectedId;
    if (pid == null || iid == null || iid <= 0) {
      this.errorService.showErrorMessage('Selecione um item de estoque na lista.');
      return;
    }
    this.linkItemSubmitting = true;
    this.stockMovementsService
      .linkPendingItemRequest(pid, { itemId: iid })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.linkItemSubmitting = false;
          this.closeLinkItemModal();
          this.errorService.showSuccessMessage('Item vinculado com sucesso.');
          this.loadData();
        },
        error: () => {
          this.linkItemSubmitting = false;
        }
      });
  }
}
